// Satser for ventetid, bomtur og avbestilling (settings/terms). Speilet i functions/src/terms.js.

export const DEFAULT_TERMS = {
  version: 1,
  confirmed: false, // plassholdere til admin har bekreftet tallene
  freeMinutes: 30,
  waitingRatePerHour: 900,
  waitingStepMinutes: 30,
  bomtur: { pct: 50, minAmount: 1000 },
  cancellationLadder: [
    { hoursBefore: 24, pct: 0 },
    { hoursBefore: 2, pct: 25 },
    { hoursBefore: 0, pct: 50 },
  ],
};

// Øvre grense for ventetid i ett krav (minutter utover fri tid).
export const MAX_WAIT_MINUTES = 480;

// Tall fra Firestore: avviser booleans, tomme/blanke strenger, null og ikke-endelige tall.
function strictNum(v) {
  if (typeof v === 'boolean' || v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function num(v, fallback, { min = 0, max = Infinity, positive = false } = {}) {
  const n = strictNum(v);
  if (n === null) return fallback;
  if (n < min || n > max || (positive && n <= 0)) return fallback;
  return n;
}

function normalizeLadder(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_TERMS.cancellationLadder;
  const steps = raw.map(s => ({ hoursBefore: strictNum(s?.hoursBefore), pct: strictNum(s?.pct) }));
  const ok = steps.every(s => s.hoursBefore !== null && s.hoursBefore >= 0 && s.pct !== null && s.pct >= 0 && s.pct <= 100);
  if (!ok) return DEFAULT_TERMS.cancellationLadder;
  return steps.sort((a, b) => b.hoursBefore - a.hoursBefore);
}

export function normalizeTerms(raw) {
  const r = raw ?? {};
  const d = DEFAULT_TERMS;
  return {
    version: num(r.version, d.version, { min: 1 }),
    confirmed: r.confirmed === true,
    freeMinutes: num(r.freeMinutes, d.freeMinutes, { max: 600 }),
    waitingRatePerHour: Math.round(num(r.waitingRatePerHour, d.waitingRatePerHour, { max: 100000 })),
    waitingStepMinutes: num(r.waitingStepMinutes, d.waitingStepMinutes, { max: 240, positive: true }),
    bomtur: {
      pct: num(r.bomtur?.pct, d.bomtur.pct, { max: 100 }),
      minAmount: Math.round(num(r.bomtur?.minAmount, d.bomtur.minAmount, { max: 1000000 })),
    },
    cancellationLadder: normalizeLadder(r.cancellationLadder),
  };
}

const toMs = (t) => {
  if (t == null) return null;
  if (typeof t === 'number') return t;
  if (t instanceof Date) return t.getTime();
  if (typeof t.toMillis === 'function') return t.toMillis();
  return null;
};

// Ventetid ut over fri tid, i hele minutter.
export function waitingMinutes({ arrivedAt, doneAt }, terms) {
  const a = toMs(arrivedAt);
  const b = toMs(doneAt);
  if (a === null || b === null || b <= a) return 0;
  const total = Math.floor((b - a) / 60000);
  return Math.min(MAX_WAIT_MINUTES, Math.max(0, total - terms.freeMinutes));
}

export function waitingAmount(minutes, terms) {
  if (!(minutes > 0)) return 0;
  const steps = Math.ceil(minutes / terms.waitingStepMinutes);
  return Math.round((steps * terms.waitingStepMinutes / 60) * terms.waitingRatePerHour);
}

export function bomturAmount(basePrice, terms) {
  return Math.round(Math.max(terms.bomtur.minAmount, (Number(basePrice) || 0) * terms.bomtur.pct / 100));
}

// Trappen: første trinn der timene til planlagt tid er minst hoursBefore. Etter planlagt tid gjelder siste trinn.
export function cancellationFee({ scheduledAt, now = new Date(), basePrice }, terms) {
  const s = toMs(scheduledAt);
  if (s === null) return { pct: 0, amount: 0 };
  const hours = (s - toMs(now)) / 3600000;
  const ladder = terms.cancellationLadder;
  const step = ladder.find(l => hours >= l.hoursBefore) ?? ladder[ladder.length - 1];
  return { pct: step.pct, amount: Math.round((Number(basePrice) || 0) * step.pct / 100) };
}

// Korte tekstlinjer for bestillingen og vilkårssiden.
export function termsSummary(terms) {
  const fmt = (n) => Number(n).toLocaleString('nb-NO');
  const ladder = terms.cancellationLadder.map((l, i, all) => {
    const next = all[i - 1];
    const range = i === 0 ? `Mer enn ${fmt(l.hoursBefore)} timer før` : l.hoursBefore === 0 ? `Under ${fmt(next.hoursBefore)} timer før` : `${fmt(l.hoursBefore)}–${fmt(next.hoursBefore)} timer før`;
    return `${range}: ${fmt(l.pct)} %`;
  }).join(', ');
  return [
    `Lastetid: ${fmt(terms.freeMinutes)} min er gratis ved henting og levering. Deretter ${fmt(terms.waitingRatePerHour)} kr per time, regnet i påbegynte ${fmt(terms.waitingStepMinutes)} min.`,
    `Bomtur: ${fmt(terms.bomtur.pct)} % av oppdragsprisen, minst ${fmt(terms.bomtur.minAmount)} kr, hvis sjåføren ikke kan laste eller losse.`,
    `Avbestilling etter at en sjåfør er tildelt: ${ladder}.`,
  ];
}
